import React, { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Button,
  Flex,
  Loading,
  Modal,
  Pagination,
  useNotifications,
} from '@decisiv/ui-components';
import { H1, H4, P } from '@decisiv/ui-components/lib/components/Typography';
import { t, Trans } from '@lingui/macro';
import { useNavigate } from 'react-router-dom';
import ExclamationCircle from '@decisiv/iconix/lib/components/ExclamationCircle';
import Pencil from '@decisiv/iconix/lib/components/Pencil';
import Trash from '@decisiv/iconix/lib/components/Trash';
import Plus from '@decisiv/iconix/lib/components/PlusCircle';
import noop from 'lodash/noop';
import { formatNumericDate, timeAgo } from '../../utils/dates';

import SharedSettingsApi from '../../api/shared_settings';
import IntegrationsApi from '../../api/integrations';

import {
  ActionsCell,
  AvatarSquare,
  DatePrimary,
  DateRelative,
  DateStack,
  IntegrationName,
  IntegrationNamesCell,
  LineDivider,
  ListContainer,
  NameCell,
  NamePrimary,
  NameSubtitle,
  PageSection,
  PageSectionHeader,
  SettingsTable,
  ShowingText,
  TableFooter,
  TableHeader,
  TableHeaderCell,
  TableRow,
} from './style';

const PAGE_SIZE = 25;

const AVATAR_COLORS = [
  '#E07B2C',
  '#3B7DD8',
  '#17A589',
  '#27AE60',
  '#8E44AD',
  '#E91E8C',
  '#2980B9',
  '#D35400',
];

function getInitials(name) {
  return (name || '')
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0].toUpperCase())
    .join('');
}

function getAvatarColor(name) {
  const hash = (name || '')
    .split('')
    .reduce((acc, c) => acc + c.charCodeAt(0), 0);
  return AVATAR_COLORS[hash % AVATAR_COLORS.length];
}

function formatDateCell(iso) {
  if (!iso) return null;
  const date = new Date(iso);
  return {
    primary: formatNumericDate(date),
    relative: timeAgo(date),
  };
}

export default function SharedSettingsList({ onCreate, onEdit }) {
  const navigate = useNavigate();
  const [settings, setSettings] = useState([]);
  const [loading, setLoading] = useState(false);
  const [integrationNames, setIntegrationNames] = useState({});
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const { notify } = useNotifications();

  const loadSettings = useCallback(async () => {
    setLoading(true);
    const {
      data,
      totalRecords: total,
      errors,
    } = await SharedSettingsApi.fetchSharedSettings({
      page: currentPage,
      limit: PAGE_SIZE,
    });
    if (errors) {
      notify(
        {
          intent: 'warning',
          title: t`Failed to load Shared Settings`,
          onClose: noop,
        },
        t`Please try again.`,
      );
      setLoading(false);
      return;
    }

    const items = (data || []).map((item) => ({
      id: item.id,
      ...item.attributes,
    }));
    setSettings(items);
    setTotalRecords(total || 0);

    const allUuids = [
      ...new Set(items.flatMap((s) => s.integration_uuids || [])),
    ];
    if (allUuids.length > 0) {
      const results = await Promise.all(
        allUuids.map(async (uuid) => {
          const { data: intData } =
            await IntegrationsApi.fetchIntegrationBasicInfo(uuid);
          return intData ? [uuid, intData.attributes?.name] : null;
        }),
      );
      setIntegrationNames((prev) => ({
        ...prev,
        ...Object.fromEntries(results.filter(Boolean)),
      }));
    }

    setLoading(false);
  }, [notify, currentPage]);

  useEffect(() => {
    loadSettings();
  }, [loadSettings]);

  const handleDeleteClick = (setting) => {
    setDeleteTarget(setting);
  };

  const handleDeleteConfirm = async () => {
    const target = deleteTarget;
    setDeleteTarget(null);
    const { errors } = await SharedSettingsApi.deleteSharedSetting(target.id);
    if (errors) {
      notify(
        {
          intent: 'warning',
          title: t`Failed to delete Shared Setting`,
          onClose: noop,
        },
        t`Please try again.`,
      );
    } else {
      notify(
        { intent: 'success', title: t`Shared Setting deleted`, onClose: noop },
        target.name,
      );
      // Step back a page when the last row on a non-first page is removed,
      // otherwise just refresh the current page from the server.
      if (settings.length === 1 && currentPage > 1) {
        setCurrentPage((page) => page - 1);
      } else {
        loadSettings();
      }
    }
  };

  const totalPages = Math.ceil(totalRecords / PAGE_SIZE);
  const pagedSettings = settings;

  return (
    <>
      <PageSection>
        <PageSectionHeader>
          <Flex flex={1}>
            <H1>
              <Trans>Shared Settings</Trans>
            </H1>
          </Flex>
          <Button text={t`Create`} icon={Plus} onClick={onCreate} />
        </PageSectionHeader>
        <P shade={1} marginTop={0.5}>
          <Trans>
            Shared Location Configuration Management eliminates the need to
            manually enter and maintain the same settings across every
            integration and location individually. Instead, use this page to
            define a shared location configuration attribute once and reference
            it everywhere, so changes are made in one place and automatically
            applied across all linked integrations.
          </Trans>
        </P>
      </PageSection>

      <LineDivider />

      <ListContainer>
        {/* eslint-disable-next-line no-nested-ternary */}
        {loading ? (
          <Flex justifyContent="center" paddingY={4}>
            <Loading title={t`Loading Shared Settings...`} />
          </Flex>
        ) : settings.length === 0 ? (
          <Flex flexDirection="column" alignItems="center" paddingY={4}>
            <P shade={1}>
              <Trans>
                No Shared Settings yet. Create one to enable automatic attribute
                mapping during propagation.
              </Trans>
            </P>
          </Flex>
        ) : (
          <SettingsTable>
            <TableHeader>
              <div />
              <TableHeaderCell>
                <Trans>Name</Trans>
              </TableHeaderCell>
              <TableHeaderCell>
                <Trans>Linked Integrations</Trans>
              </TableHeaderCell>
              <TableHeaderCell>
                <Trans>Created</Trans>
              </TableHeaderCell>
              <TableHeaderCell>
                <Trans>Updated</Trans>
              </TableHeaderCell>
              <TableHeaderCell style={{ textAlign: 'right' }}>
                <Trans>Actions</Trans>
              </TableHeaderCell>
            </TableHeader>

            {pagedSettings.map((setting) => {
              const linkedCount = setting.integration_uuids?.length || 0;
              const attrCount = setting.mappings?.length || 0;
              const created = formatDateCell(setting.created_at);
              const updated = formatDateCell(setting.updated_at);
              return (
                <TableRow key={setting.id}>
                  <AvatarSquare $color={getAvatarColor(setting.name)}>
                    {getInitials(setting.name)}
                  </AvatarSquare>

                  <NameCell>
                    <NamePrimary>{setting.name}</NamePrimary>
                    <NameSubtitle>
                      {linkedCount}{' '}
                      {linkedCount === 1 ? (
                        <Trans>integration</Trans>
                      ) : (
                        <Trans>integrations</Trans>
                      )}
                      {attrCount > 0 && (
                        <>
                          {' · '}
                          {attrCount}{' '}
                          {attrCount === 1 ? (
                            <Trans>attribute</Trans>
                          ) : (
                            <Trans>attributes</Trans>
                          )}
                        </>
                      )}
                    </NameSubtitle>
                  </NameCell>

                  <IntegrationNamesCell>
                    {(setting.integration_uuids || [])
                      .slice(0, 3)
                      .map((uuid) => (
                        <IntegrationName key={uuid}>
                          {integrationNames[uuid] || uuid}
                        </IntegrationName>
                      ))}
                    {linkedCount > 3 && (
                      <IntegrationName $muted>
                        +{linkedCount - 3} <Trans>more</Trans>
                      </IntegrationName>
                    )}
                  </IntegrationNamesCell>

                  {created ? (
                    <DateStack>
                      <DatePrimary>{created.primary}</DatePrimary>
                      <DateRelative>{created.relative}</DateRelative>
                    </DateStack>
                  ) : (
                    <span>—</span>
                  )}

                  {updated ? (
                    <DateStack>
                      <DatePrimary>{updated.primary}</DatePrimary>
                      <DateRelative>{updated.relative}</DateRelative>
                    </DateStack>
                  ) : (
                    <span>—</span>
                  )}

                  <ActionsCell>
                    <Button
                      aria-label={t`Needs attention in ${setting.name}`}
                      icon={ExclamationCircle}
                      kind="secondary"
                      size="small"
                      onClick={() =>
                        navigate(
                          `/toolkit/shared_settings/${setting.id}/needs_attention`,
                        )
                      }
                    />
                    <Button
                      aria-label={t`Edit ${setting.name}`}
                      icon={Pencil}
                      kind="secondary"
                      size="small"
                      onClick={() => onEdit(setting.id)}
                    />
                    <Button
                      aria-label={t`Delete ${setting.name}`}
                      icon={Trash}
                      kind="secondary"
                      size="small"
                      intent="danger"
                      onClick={() => handleDeleteClick(setting)}
                    />
                  </ActionsCell>
                </TableRow>
              );
            })}

            <TableFooter>
              <ShowingText>
                <Trans>Showing</Trans> {pagedSettings.length} <Trans>of</Trans>{' '}
                {totalRecords} <Trans>settings</Trans>
              </ShowingText>
              {totalPages > 1 && (
                <Pagination
                  totalPages={totalPages}
                  activePage={currentPage}
                  onPageChange={setCurrentPage}
                />
              )}
            </TableFooter>
          </SettingsTable>
        )}
      </ListContainer>

      <Modal
        icon={Trash}
        intent="danger"
        id="delete-shared-setting-modal"
        onClose={() => setDeleteTarget(null)}
        title={t`Delete Shared Setting`}
        visible={!!deleteTarget}
        actions={[
          {
            text: t`Delete`,
            intent: 'danger',
            onClick: handleDeleteConfirm,
          },
          {
            text: t`Cancel`,
            onClick: () => setDeleteTarget(null),
          },
        ]}
      >
        <Flex flexDirection="column">
          <H4 marginBottom={0.5}>{deleteTarget?.name}</H4>
          <P shade={1} marginTop={1}>
            <Trans>
              Are you sure you want to delete this Shared Setting? This action
              cannot be undone.
            </Trans>
          </P>
        </Flex>
      </Modal>
    </>
  );
}

SharedSettingsList.propTypes = {
  onCreate: PropTypes.func.isRequired,
  onEdit: PropTypes.func.isRequired,
};
